import React, { useEffect, useState, useRef } from 'react';
import Slider from 'react-slick';
import '../../Content/slickCSS.css';
import { styled } from 'linaria/react';
import { ReactComponent as Arrow } from '../../../svg/Arrow.svg';
import { ReactComponent as Expand } from '../../../svg/Expand.svg';
import { ReactComponent as Compress } from '../../../svg/Compress.svg';
import { theme } from '../../Theme';
import Image from '@jetshop/ui/Image/';
import { ThumbSlider } from './ThumbSlider';
import Video from '../../Content/Video';

const CustomArrow = styled(Arrow)`
  height: 18px;
  width: 18px;
`;

const ArrowWrapper = styled('div')`
  height: 40px;
  width: 40px;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  position: absolute;
  top: 35%;
  transform: translateY(-50%);
  z-index: 1;
  background: white;

  :active {
    background: #f5f5f5;
  }

  &.next {
    transform: rotate(270deg);
    right: -15px;
    left: auto;
    ${theme.below.md} {
      right: 0;
    }
  }

  &.prev {
    transform: rotate(90deg);
    right: auto;
    left: -15px;
    ${theme.below.md} {
      left: 0;
    }
  }
`;

const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <ArrowWrapper className={className + ' prev'} onClick={onClick}>
      <CustomArrow />
    </ArrowWrapper>
  );
};

const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <ArrowWrapper className={className + ' next'} onClick={onClick}>
      <CustomArrow />
    </ArrowWrapper>
  );
};

const ZoomWrapper = styled('div')`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: ${(props) => (props.$canPan ? 'none' : 'auto')};
  user-select: none;
  cursor: ${(props) => (props.$canPan ? 'grab' : 'default')};

  &:active {
    cursor: ${(props) => (props.$canPan ? 'grabbing' : 'default')};
  }

  picture {
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    height: 100%;
    width: 100%;
    max-width: none;
    max-height: 100%;
    object-fit: cover;
    pointer-events: none;
    user-select: none;
  }
`;

const ZoomInner = styled('div')`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
`;

const ZoomControls = styled('div')`
  position: absolute;
  left: 16px;
  bottom: 20px;
  z-index: 1002;
  display: flex;
  gap: 8px;
  pointer-events: auto;

  button {
    width: 40px;
    height: 40px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.1);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    line-height: 1;

    :disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }

    :active {
      background: #f5f5f5;
    }
  }
`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const EPS = 1e-6;
const ZOOM_LEVELS = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6];

const getPanBounds = ({ width, height, scale }) => {
  if (scale <= 1) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  const maxX = ((scale - 1) * width) / 2;
  const maxY = ((scale - 1) * height) / 2;
  return { minX: -maxX, maxX, minY: -maxY, maxY };
};

const ZoomableFullscreenSlide = ({ children }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const lastPointerActivateRef = useRef(0);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });

  const applyClamp = (nextScale, nextPos) => {
    const el = containerRef.current;
    if (!el) return { scale: nextScale, pos: nextPos };
    const rect = el.getBoundingClientRect();
    const bounds = getPanBounds({
      width: rect.width,
      height: rect.height,
      scale: nextScale,
    });
    return {
      scale: nextScale,
      pos: {
        x: clamp(nextPos.x, bounds.minX, bounds.maxX),
        y: clamp(nextPos.y, bounds.minY, bounds.maxY),
      },
    };
  };

  const setZoom = (nextScale, nextPos = pos) => {
    const minScale = ZOOM_LEVELS[0];
    const maxScale = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
    const clamped = applyClamp(clamp(nextScale, minScale, maxScale), nextPos);
    setScale(clamped.scale);
    setPos(clamped.pos);
  };

  const nextZoomInLevel = () => {
    for (const level of ZOOM_LEVELS) {
      if (level > scale + EPS) return level;
    }
    return ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
  };

  const nextZoomOutLevel = () => {
    for (let i = ZOOM_LEVELS.length - 1; i >= 0; i--) {
      const level = ZOOM_LEVELS[i];
      if (level < scale - EPS) return level;
    }
    return ZOOM_LEVELS[0];
  };

  const canZoomIn = scale < ZOOM_LEVELS[ZOOM_LEVELS.length - 1] - EPS;
  const canZoomOut = scale > ZOOM_LEVELS[0] + EPS;

  const zoomIn = () => {
    if (!canZoomIn) return;
    setZoom(nextZoomInLevel());
  };
  const zoomOut = () => {
    if (!canZoomOut) return;
    setZoom(nextZoomOutLevel());
  };
  const reset = () => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  };

  const onDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (scale <= 1 + EPS) zoomIn();
    else reset();
  };

  const onWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY ?? 0;
    if (delta < 0) zoomIn();
    if (delta > 0) zoomOut();
  };

  const onPointerDown = (e) => {
    if (scale <= 1 + EPS) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseX: pos.x,
      baseY: pos.y,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== e.pointerId) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const nextPos = {
      x: dragRef.current.baseX + dx,
      y: dragRef.current.baseY + dy,
    };
    const clamped = applyClamp(scale, nextPos);
    setPos(clamped.pos);
  };

  const endDrag = (e) => {
    if (dragRef.current.pointerId !== e.pointerId) return;
    dragRef.current.active = false;
    dragRef.current.pointerId = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  return (
    <ZoomWrapper
      ref={containerRef}
      $canPan={scale > 1 + EPS}
      onDoubleClick={onDoubleClick}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <ZoomInner
        style={{
          transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`,
        }}
      >
        {children}
      </ZoomInner>
      <ZoomControls
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <button
          type="button"
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            lastPointerActivateRef.current = Date.now();
            zoomOut();
          }}
          onClick={(e) => {
            // Avoid double-trigger (pointer -> click) on mobile
            if (Date.now() - lastPointerActivateRef.current < 500) return;
            e.preventDefault();
            e.stopPropagation();
            zoomOut();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-label="Zoom out"
          disabled={!canZoomOut}
        >
          −
        </button>
        <button
          type="button"
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            lastPointerActivateRef.current = Date.now();
            reset();
          }}
          onClick={(e) => {
            if (Date.now() - lastPointerActivateRef.current < 500) return;
            e.preventDefault();
            e.stopPropagation();
            reset();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-label="Reset zoom"
          disabled={scale <= 1 + EPS}
        >
          ⟲
        </button>
        <button
          type="button"
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            lastPointerActivateRef.current = Date.now();
            zoomIn();
          }}
          onClick={(e) => {
            if (Date.now() - lastPointerActivateRef.current < 500) return;
            e.preventDefault();
            e.stopPropagation();
            zoomIn();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-label="Zoom in"
          disabled={!canZoomIn}
        >
          +
        </button>
      </ZoomControls>
    </ZoomWrapper>
  );
};

const FullscreenButton = styled('button')`
  position: absolute;
  top: auto;
  bottom: 0px;
  right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 1;
  transition: all 0.2s ease;

  :active {
    background: #f5f5f5;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: #333;
  }
`;

const FullscreenModal = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;

  .close-button {
    position: absolute;
    top: auto;
    bottom: 48px;
    right: 48px;
    width: 40px;
    height: 40px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1001;
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);

    ${theme.below.lg} {
      bottom: 30px;
      right: 20px;
    }

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    :active {
      background: #f5f5f5;
    }

    svg {
      width: 20px;
      height: 20px;
      fill: #333;
    }
  }

  .fullscreen-slider {
    width: 100%;
    max-width: 98vw;
    max-height: 98vh;

    .slick-slide {
      > div {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 95dvh;
        max-height: 95dvh;
        width: 100%;

        ${theme.below.lg} {
          height: 98dvh;
          max-height: 98dvh;
        }

        .charpstar-button-container {
          right: auto !important;
          left: 12px !important;
          button {
            padding: 4px 12px 4px 4px !important;
            min-width: fit-content !important;

            &[data-tech='charpstar-ar'] svg {
              padding-right: 0px !important;
            }
          }
        }

        .video {
          max-width: 100%;
          max-height: 100%;
        }
      }
    }

    .slick-prev,
    .slick-next {
      width: 40px !important;
      height: 40px !important;
      border-radius: 20px !important;
      background: rgba(255, 255, 255, 0.9) !important;
      z-index: 1001 !important;
      top: auto !important;
      bottom: 20px !important;
      transform: translateY(0) !important;

      &:hover {
        background: rgba(255, 255, 255, 1) !important;
      }

      svg {
        width: 18px !important;
        height: 18px !important;
      }
    }

    .slick-prev {
      left: auto !important;
      right: 130px !important;
      transform: rotate(90deg) !important;
    }

    .slick-next {
      right: 78px !important;

      transform: rotate(270deg) !important;
    }
  }
`;

const Gallery360Wrapper = styled('div')`
  position: relative;
  scroll-margin-top: 0;

  /* Prevent scroll behavior when focusing on thumb slider */
  .thumbs-slider-wrapper {
    scroll-margin-top: 0;

    * {
      &:focus {
        scroll-margin-top: 0;
      }
    }
  }

  .slick-prev {
    bottom: 0px !important;
    right: 116px !important;
    top: auto !important;
    left: auto !important;
  }

  .slick-next {
    bottom: 0px !important;
    right: 64px !important;
    top: auto !important;
    left: auto !important;
  }

  .slick-arrow::before {
    content: '' !important;
  }

  .slick-slide {
    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      aspect-ratio: ${(props) => props.threesixtyaspect};

      .charpstar-button-container {
        right: auto !important;
        left: 12px !important;
        ${theme.below.xl} {
          bottom: 0px !important;
        }

        button {
          padding: 2px 6px 2px 2px !important;
          min-width: fit-content !important;

          &[data-tech='charpstar-ar'] svg {
            padding-right: 0px !important;
          }
        }
      }
    }

    &:has(> div > .video) > div {
      /* Constrain video to container height */
      overflow: hidden;

      .video {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        margin: 0 auto;
      }
    }
  }
`;

// Rearrange media so videos are always on the second slide
const rearrangeMedia = (mediaArray, include360 = false) => {
  const type360 = '360';
  if (!mediaArray || mediaArray.length === 0) {
    return mediaArray || [];
  }

  // Separate videos from other media
  const videos = mediaArray.filter((m) => m?.type === 'video');
  const nonVideoMedia = mediaArray.filter(
    (m) => m?.type !== 'video' && m?.type !== type360
  );
  const existing360 = mediaArray.filter((m) => m?.type === type360);

  if (videos.length === 0) {
    // No videos, just return with 360 if needed
    if (include360 && existing360.length === 0) {
      return [{ type: type360 }, ...nonVideoMedia];
    }
    return mediaArray;
  }

  // Rearrange: videos should be on second slide
  // If 360 exists: [360, video, ...images]
  // If no 360: [first image, video, ...rest of images]
  let rearrangedMedia = [];

  if (include360 || existing360.length > 0) {
    // [360, video, ...images]
    const three60 = existing360.length > 0 ? existing360[0] : { type: type360 };
    rearrangedMedia = [three60, ...videos, ...nonVideoMedia];
  } else {
    // [first image, video, ...rest of images]
    if (nonVideoMedia.length > 0) {
      const firstImage = nonVideoMedia[0];
      const restImages = nonVideoMedia.slice(1);
      rearrangedMedia = [firstImage, ...videos, ...restImages];
    } else {
      rearrangedMedia = [...videos];
    }
  }

  return rearrangedMedia;
};

export const Gallery360 = ({ mediaList, variant, aspect }) => {
  const type360 = '360';
  const [has360View, setHas360View] = useState(false);
  // Initialize media with rearranged order
  const [media, setMedia] = useState(() =>
    rearrangeMedia(mediaList || [], false)
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState(null);

  useEffect(() => {
    const rearranged = rearrangeMedia(mediaList || [], has360View);
    setMedia(rearranged);
  }, [mediaList, has360View]);
  const mainRef = useRef(null);
  const thumbsRef = useRef(null);
  const fullscreenRef = useRef(null);
  const [navMain, setNavMain] = useState(null);
  const [navThumbs, setNavThumbs] = useState(null);
  const previousVariantRef = useRef(null);
  const currentSlideIndexRef = useRef(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    // Set navigation references with a delay to ensure sliders are rendered
    const timer = setTimeout(() => {
      if (mainRef.current) {
        setNavMain(mainRef.current);
      }
      if (thumbsRef.current) {
        setNavThumbs(thumbsRef.current);
        // Sync thumb slider to current slide after ref is set
        if (mainRef.current && thumbsRef.current) {
          const currentSlide =
            mainRef.current.innerSlider?.state?.currentSlide ??
            currentSlideIndexRef.current;
          currentSlideIndexRef.current = currentSlide;
          setCurrentSlideIndex(currentSlide);
          setTimeout(() => {
            if (thumbsRef.current?.slickGoTo) {
              // Store current scroll position to prevent scroll jump
              const scrollY = window.scrollY;
              const scrollX = window.scrollX;

              thumbsRef.current.slickGoTo(currentSlide, false);

              // Restore scroll position after slider navigation
              requestAnimationFrame(() => {
                window.scrollTo(scrollX, scrollY);
              });
            }
          }, 200);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [media, has360View]); // Re-run when media or 360 view status changes

  // Handle resize to sync sliders
  useEffect(() => {
    const handleResize = () => {
      // Clear any pending resize timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        // Re-establish navigation refs after resize
        if (mainRef.current) {
          setNavMain(mainRef.current);
          // Get current slide from main slider
          const currentSlide =
            mainRef.current.innerSlider?.state?.currentSlide ??
            currentSlideIndexRef.current;
          currentSlideIndexRef.current = currentSlide;
          setCurrentSlideIndex(currentSlide);
        }
        if (thumbsRef.current) {
          setNavThumbs(thumbsRef.current);
          // Sync thumb slider to current slide
          const currentSlide = currentSlideIndexRef.current;
          setTimeout(() => {
            if (thumbsRef.current?.slickGoTo && mainRef.current) {
              // Store current scroll position to prevent scroll jump
              const scrollY = window.scrollY;
              const scrollX = window.scrollX;

              thumbsRef.current.slickGoTo(currentSlide, false);

              // Restore scroll position after slider navigation
              requestAnimationFrame(() => {
                window.scrollTo(scrollX, scrollY);
              });
            }
          }, 150);
        }
      }, 300);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: mediaList?.length > 1 ? 1.1 : 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    draggable: false,
    centerMode: true,
    centerPadding: '8%',
    dots: false,
    asNavFor: navThumbs,
    afterChange: (currentSlide) => {
      currentSlideIndexRef.current = currentSlide;
      setCurrentSlideIndex(currentSlide);
      // Sync thumb slider
      if (thumbsRef.current?.slickGoTo) {
        // Store current scroll position to prevent scroll jump
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        thumbsRef.current.slickGoTo(currentSlide, false);

        // Restore scroll position after slider navigation
        requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
        });
      }
    },
    responsive: [
      {
        breakpoint: 1200,
        settings: {},
      },
      {
        breakpoint: 900,
        settings: {},
      },
      {
        breakpoint: 600,
        settings: {},
      },
    ],
  };

  const fullscreenSettings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    draggable: false,
    centerMode: false,
    dots: false,
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  useEffect(() => {
    const checkFor360View = async () => {
      if (variant?.articleNumber) {
        const waitForCharpstAR = () => {
          return new Promise((resolve) => {
            if (window.charpstAR?.checkProductStatus) {
              resolve(window.charpstAR);
            } else {
              const interval = setInterval(() => {
                if (window.charpstAR?.checkProductStatus) {
                  clearInterval(interval);
                  resolve(window.charpstAR);
                }
              }, 100);

              setTimeout(() => {
                clearInterval(interval);
                resolve(null);
              }, 5000);
            }
          });
        };

        try {
          const charpstAR = await waitForCharpstAR();
          if (charpstAR) {
            const has360 = await charpstAR.checkProductStatus(
              variant.articleNumber
            );
            setHas360View(has360);
          } else {
            setHas360View(false);
          }
        } catch (error) {
          setHas360View(false);
        }
      } else {
        setHas360View(false);
      }
    };

    checkFor360View();
  }, [variant?.articleNumber]);

  useEffect(() => {
    if (has360View && variant?.articleNumber) {
      const initializeCharpstAR = async () => {
        const waitForCharpstAR = () => {
          return new Promise((resolve) => {
            if (window.charpstAR?.init) {
              resolve(window.charpstAR);
            } else {
              const interval = setInterval(() => {
                if (window.charpstAR?.init) {
                  clearInterval(interval);
                  resolve(window.charpstAR);
                }
              }, 100);

              setTimeout(() => {
                clearInterval(interval);
                resolve(null);
              }, 5000);
            }
          });
        };

        try {
          const charpstAR = await waitForCharpstAR();
          if (charpstAR) {
            setTimeout(() => {
              if (charpstAR.init) {
                charpstAR.init();
              }
              if (charpstAR.refresh) {
                charpstAR.refresh();
              }

              const mediaContains360 = media.some((m) => m.type === type360);
              if (!mediaContains360) {
                // Use rearrangeMedia to maintain video position (second slide)
                const rearranged = rearrangeMedia(media, true);
                setMedia(rearranged);
              }
            }, 200);
          }
        } catch (error) {
          // Silent fail
        }
      };

      initializeCharpstAR();
    }
  }, [has360View, variant?.articleNumber, media]);

  useEffect(() => {
    if (isFullscreen && has360View && variant?.articleNumber) {
      const initializeFullscreenCharpstAR = async () => {
        const waitForCharpstAR = () => {
          return new Promise((resolve) => {
            if (window.charpstAR?.init) {
              resolve(window.charpstAR);
            } else {
              const interval = setInterval(() => {
                if (window.charpstAR?.init) {
                  clearInterval(interval);
                  resolve(window.charpstAR);
                }
              }, 100);

              setTimeout(() => {
                clearInterval(interval);
                resolve(null);
              }, 5000);
            }
          });
        };

        try {
          const charpstAR = await waitForCharpstAR();
          if (charpstAR) {
            setTimeout(() => {
              if (charpstAR.init) {
                charpstAR.init();
              }
              if (charpstAR.refresh) {
                charpstAR.refresh();
              }
            }, 500);
          }
        } catch (error) {
          // Silent fail
        }
      };

      initializeFullscreenCharpstAR();
    }
  }, [isFullscreen, has360View, variant?.articleNumber]);

  useEffect(() => {
    if (!isFullscreen && has360View && variant?.articleNumber) {
      const initializeMainGalleryCharpstAR = async () => {
        const waitForCharpstAR = () => {
          return new Promise((resolve) => {
            if (window.charpstAR?.init) {
              resolve(window.charpstAR);
            } else {
              const interval = setInterval(() => {
                if (window.charpstAR?.init) {
                  clearInterval(interval);
                  resolve(window.charpstAR);
                }
              }, 100);

              setTimeout(() => {
                clearInterval(interval);
                resolve(null);
              }, 5000);
            }
          });
        };

        try {
          const charpstAR = await waitForCharpstAR();
          if (charpstAR) {
            setTimeout(() => {
              if (charpstAR.init) {
                charpstAR.init();
              }
              if (charpstAR.refresh) {
                charpstAR.refresh();
              }
            }, 300);
          }
        } catch (error) {
          // Silent fail
        }
      };

      initializeMainGalleryCharpstAR();
    }
  }, [isFullscreen, has360View, variant?.articleNumber]);

  useEffect(() => {
    // When variant changes (not on initial mount), navigate to the appropriate slide
    if (!variant?.articleNumber || !mainRef?.current) return;

    // Check if variant actually changed (not initial mount)
    const variantChanged =
      previousVariantRef.current !== null &&
      previousVariantRef.current !== variant?.articleNumber;

    // Update the ref for next comparison
    previousVariantRef.current = variant?.articleNumber;

    // Only navigate if variant changed (skip initial mount)
    if (!variantChanged) return;

    const navigateToSlide = (targetIndex) => {
      setTimeout(() => {
        // Store current scroll position to prevent scroll jump
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        if (mainRef?.current?.slickGoTo) {
          mainRef?.current?.slickGoTo(targetIndex, false);
          currentSlideIndexRef.current = targetIndex;
          setCurrentSlideIndex(targetIndex);
        }
        // Sync thumb slider
        if (thumbsRef?.current?.slickGoTo) {
          thumbsRef.current.slickGoTo(targetIndex, false);
        }
        // Also update fullscreen slider if it's open
        if (isFullscreen && fullscreenRef?.current?.slickGoTo) {
          fullscreenRef.current.slickGoTo(targetIndex, false);
        }

        // Restore scroll position after slider navigation
        requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
        });
      }, 100);
    };

    if (has360View) {
      // If there's a 360 view, slide to the first slide (index 0)
      navigateToSlide(0);
    } else if (variant?.images?.[0]?.url) {
      // If no 360 view, find the first image that matches the variant's first image
      const targetImageUrl = variant?.images?.[0]?.url;
      const matchingIndex = media?.findIndex((m) => m?.url === targetImageUrl);

      if (matchingIndex !== -1) {
        navigateToSlide(matchingIndex);
      }
    }
  }, [variant?.articleNumber, has360View, media, isFullscreen]);

  const sizes = [1, 1, 1, 1 / 2, 1 / 2];

  const adjustAspectRatio = (aspect) => {
    return '6/5';
  };

  return (
    <>
      <Gallery360Wrapper threesixtyaspect={adjustAspectRatio(aspect)}>
        <div className="main-slider-container" style={{ position: 'relative' }}>
          <FullscreenButton
            onClick={handleFullscreenToggle}
            aria-label="Open fullscreen"
          >
            <Expand />
          </FullscreenButton>
          <Slider {...settings} ref={mainRef}>
            {media?.map((m, index) => {
              if (m?.type === type360) {
                if (!isFullscreen) {
                  return (
                    <charpstar-container
                      key={`charpstar-${variant?.articleNumber}-${index}`}
                      data-articleid={variant?.articleNumber}
                    ></charpstar-container>
                  );
                } else {
                  return (
                    <div
                      key={`placeholder-${variant?.articleNumber}-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        fontSize: '14px',
                      }}
                    >
                      360° View Available in Fullscreen
                    </div>
                  );
                }
              }

              if (m?.type === 'video') {
                return (
                  <Video
                    {...m}
                    setVideoThumbnail={setVideoThumbnail}
                    fillHeight={true}
                  />
                );
              }
              return (
                <Image
                  src={m.url}
                  sizes={sizes}
                  aspect={'6:5'}
                  alt={m?.alt}
                  key={`slide-${m.url || index}`}
                />
              );
            })}
          </Slider>
        </div>
        <ThumbSlider
          media={media}
          videoThumbnail={videoThumbnail}
          aspect={'6:5'}
          thumbsRef={thumbsRef}
          navMain={navMain}
          currentSlideIndex={currentSlideIndex}
        />
      </Gallery360Wrapper>

      {isFullscreen && (
        <FullscreenModal onClick={handleCloseFullscreen}>
          <button
            className="close-button"
            onClick={handleCloseFullscreen}
            aria-label="Close fullscreen"
          >
            <Compress />
          </button>
          <div
            className="fullscreen-slider"
            onClick={(e) => e.stopPropagation()}
          >
            <Slider {...fullscreenSettings} ref={fullscreenRef}>
              {media?.map((m, index) => {
                if (m?.type === type360) {
                  if (isFullscreen) {
                    return (
                      <charpstar-container
                        key={`fullscreen-charpstar-${variant?.articleNumber}-${index}`}
                        data-articleid={variant?.articleNumber}
                      ></charpstar-container>
                    );
                  } else {
                    return (
                      <div
                        key={`fullscreen-placeholder-${variant?.articleNumber}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                          fontSize: '14px',
                        }}
                      >
                        360° View
                      </div>
                    );
                  }
                }

                if (m?.type === 'video') {
                  return (
                    <Video
                      {...m}
                      setVideoThumbnail={setVideoThumbnail}
                      fillHeight={true}
                    />
                  );
                }
                return (
                  <ZoomableFullscreenSlide
                    key={`fullscreen-slide-${m.url || index}`}
                  >
                    <Image
                      className="fullscreen-image"
                      src={m.url}
                      // sizes={[1, 1, 1, 1, 1]}
                      aspect={null}
                      cover
                      alt={m?.alt}
                      quality={100}
                    />
                  </ZoomableFullscreenSlide>
                );
              })}
            </Slider>
          </div>
        </FullscreenModal>
      )}
    </>
  );
};
